import {
	Card,
	Table,
	Button,
	Space,
	Typography,
	Input,
	Select,
	Tag,
	Tooltip,
	Dropdown,
	Modal,
	notification,
	Row,
	Col,
	Statistic,
	Badge,
} from "antd";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiGlobeDuotone,
	PiPlusDuotone,
	PiMagnifyingGlassDuotone,
	PiEyeDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiStarDuotone,
	PiCrownDuotone,
	PiTrendUpDuotone,
	PiSelectionAllDuotone,
	PiHouseDuotone,
	PiFunnelDuotone,
} from "react-icons/pi";
import {
	requestGetDomainExtensionList,
	requestDeleteDomainExtension,
} from "../../services/admin.service";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const CATEGORY_COLORS = {
	GENERIC: "blue",
	COUNTRY: "green",
	SPONSORED: "purple",
	NEW_GENERIC: "orange",
	REGIONAL: "cyan",
	OTHER: "default",
};

const STATUS_COLORS = {
	ACTIVE: "success",
	INACTIVE: "default",
	COMING_SOON: "processing",
	DEPRECATED: "warning",
};

function DomainExtensionsPage() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [statsLoading, setStatsLoading] = useState(false);

	const [domainExtensions, setDomainExtensions] = useState([]);
	const [stats, setStats] = useState(null);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});

	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
		category: searchParams.get("category") || "",
		status: searchParams.get("status") || "",
		isPopular: searchParams.get("isPopular") ? searchParams.get("isPopular") === "true" : null,
		isFeatured: searchParams.get("isFeatured") ? searchParams.get("isFeatured") === "true" : null,
	});

	useEffect(() => {
		document.title = "Cloudiva Console | Domain Extensions";
		fetchDomainExtensions();
	}, []);

	useEffect(() => {
		fetchStats();
	}, []);

	// Refetch data when pagination or filters change
	useEffect(() => {
		fetchDomainExtensions();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);
		if (filters.category) params.set("category", filters.category);
		if (filters.status) params.set("status", filters.status);
		if (filters.isPopular !== null) params.set("isPopular", filters.isPopular.toString());
		if (filters.isFeatured !== null) params.set("isFeatured", filters.isFeatured.toString());

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const buildFilterString = () => {
		const filterParts = [];
		if (filters.category) filterParts.push(`category:${filters.category}`);
		if (filters.status) filterParts.push(`status:${filters.status}`);
		if (filters.isPopular !== null) filterParts.push(`isPopular:${filters.isPopular}`);
		if (filters.isFeatured !== null) filterParts.push(`isFeatured:${filters.isFeatured}`);
		return filterParts.join(",");
	};

	const fetchDomainExtensions = async () => {
		setLoading(true);
		try {
			const params = {
				page: pagination.current,
				limit: pagination.pageSize,
				keyword: filters.keyword,
				order: "name:asc",
				filter: buildFilterString(),
			};

			const response = await requestGetDomainExtensionList(params);

			// Server response structure: { code, message, pagination, data }
			const domainExtensionsData = response.data.data || [];
			const serverPagination = response.data.pagination || {};

			setDomainExtensions(domainExtensionsData);
			setPagination((prev) => ({
				...prev,
				total: serverPagination.total || 0,
			}));
		} catch (error) {
			console.error("Error fetching domain extensions:", error);
			notification.error({
				message: "Error",
				description: "Failed to fetch domain extensions",
			});
		} finally {
			setLoading(false);
		}
	};

	const fetchStats = async () => {
		setStatsLoading(true);
		try {
			// const response = await requestGetDomainExtensionStats();
			// setStats(response.data);
		} catch (error) {
			console.error("Error fetching stats:", error);
		} finally {
			setStatsLoading(false);
		}
	};

	const handleTableChange = (pagination) => {
		setPagination(pagination);
	};

	const handleSearch = (value) => {
		setFilters((prev) => ({ ...prev, keyword: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleFilterChange = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleDelete = async (id, extension) => {
		Modal.confirm({
			title: "Delete Domain Extension",
			content: `Are you sure you want to delete the "${extension}" domain extension?`,
			okText: "Yes, Delete",
			cancelText: "Cancel",
			okType: "danger",
			onOk: async () => {
				try {
					await requestDeleteDomainExtension(id);
					notification.success({
						message: "Success",
						description: "Domain extension deleted successfully",
					});
					fetchDomainExtensions();
					fetchStats();
				} catch (error) {
					console.error("Error deleting domain extension:", error);
					notification.error({
						message: "Error",
						description: error.response?.data?.message || "Failed to delete domain extension",
					});
				}
			},
		});
	};

	const handleBulkUpdate = async (action, value) => {
		if (selectedRowKeys.length === 0) {
			notification.warning({
				message: "Warning",
				description: "Please select at least one domain extension",
			});
			return;
		}

		try {
			// await requestBulkUpdateDomainExtensions({
			// 	ids: selectedRowKeys,
			// 	action,
			// 	value,
			// });
			// notification.success({
			// 	message: "Success",
			// 	description: `Bulk ${action} updated successfully`,
			// });
			// setSelectedRowKeys([]);
			// fetchDomainExtensions();
			// fetchStats();
		} catch (error) {
			console.error("Error bulk updating:", error);
			notification.error({
				message: "Error",
				description: error.response?.data?.message || "Failed to update domain extensions",
			});
		}
	};

	const columns = [
		{
			title: "Extension",
			dataIndex: "name",
			key: "name",
			width: 150,
			render: (name, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiGlobeDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{name}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>
							{record.extension} • ID: {record._id.substring(0, 8)}...
						</div>
					</div>
				</div>
			),
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			width: 120,
			render: (category) => (
				<Tag color={CATEGORY_COLORS[category]} style={{ borderRadius: "12px" }}>
					{category.replace(/_/g, " ")}
				</Tag>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (status) => (
				<Tag color={STATUS_COLORS[status]} style={{ borderRadius: "12px" }}>
					{status.replace(/_/g, " ")}
				</Tag>
			),
		},
		{
			title: "Pricing",
			key: "pricing",
			width: 180,
			render: (_, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>
						{formatCurrency(record.price?.registration?.amount || 0)}
					</div>
					<div style={{ fontSize: "12px", color: "#666" }}>
						Renewal: {formatCurrency(record.price?.renewal?.amount || 0)} • Transfer:{" "}
						{formatCurrency(record.price?.transfer?.amount || 0)}
					</div>
				</div>
			),
		},
		{
			title: "Features",
			key: "features",
			width: 120,
			render: (_, record) => (
				<Space direction="vertical" size={2}>
					{record.popularity?.isPopular && (
						<Tag color="gold" size="small" style={{ borderRadius: "8px" }}>
							<PiStarDuotone size={12} style={{ marginRight: 2 }} />
							Popular
						</Tag>
					)}
					{record.popularity?.isFeatured && (
						<Tag color="purple" size="small" style={{ borderRadius: "8px" }}>
							<PiCrownDuotone size={12} style={{ marginRight: 2 }} />
							Featured
						</Tag>
					)}
				</Space>
			),
		},
		{
			title: "Popularity",
			dataIndex: ["popularity", "rank"],
			key: "popularity",
			width: 100,
			render: (rank, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>#{rank}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>
						{record.popularity?.orderCount || 0} orders
					</div>
				</div>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: 80,
			fixed: "right",
			align: "right",
			render: (_, record) => (
				<Space>
					<Tooltip title="View Details">
						<Button
							type="text"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/domain-extensions/${record._id}`)}
						/>
					</Tooltip>
					<Tooltip title="Edit">
						<Button
							type="text"
							icon={<PiPencilDuotone />}
							onClick={() => navigate(`/domain-extensions/${record._id}/edit`)}
						/>
					</Tooltip>
					<Tooltip title="Delete">
						<Button
							type="text"
							danger
							icon={<PiTrashDuotone />}
							onClick={() => handleDelete(record._id, record.name)}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	const rowSelection = {
		selectedRowKeys,
		onChange: setSelectedRowKeys,
		getCheckboxProps: (record) => ({
			name: record.name,
		}),
	};

	const bulkActions = [
		{
			key: "activate",
			label: "Activate Selected",
			onClick: () => handleBulkUpdate("status", "ACTIVE"),
		},
		{
			key: "deactivate",
			label: "Deactivate Selected",
			onClick: () => handleBulkUpdate("status", "INACTIVE"),
		},
		{
			key: "feature",
			label: "Mark as Featured",
			onClick: () => handleBulkUpdate("featured", true),
		},
		{
			key: "unfeature",
			label: "Remove from Featured",
			onClick: () => handleBulkUpdate("featured", false),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Domain Extension Management
					</Title>
					<Badge count={pagination.total} showZero></Badge>
				</Space>
			</div>

			<div className="page-content-wrapper">
				{/* Stats Cards */}
				{stats && (
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col xs={24} sm={12} md={6}>
							<Card size="small" loading={statsLoading}>
								<Statistic
									title="Total Extensions"
									value={stats.total}
									prefix={<PiGlobeDuotone style={{ color: "#1890ff" }} />}
								/>
							</Card>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Card size="small" loading={statsLoading}>
								<Statistic
									title="Active"
									value={stats.active}
									valueStyle={{ color: "#52c41a" }}
									prefix={<PiTrendUpDuotone />}
								/>
							</Card>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Card size="small" loading={statsLoading}>
								<Statistic
									title="Popular"
									value={stats.popular}
									valueStyle={{ color: "#faad14" }}
									prefix={<PiStarDuotone />}
								/>
							</Card>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Card size="small" loading={statsLoading}>
								<Statistic
									title="Featured"
									value={stats.featured}
									valueStyle={{ color: "#722ed1" }}
									prefix={<PiCrownDuotone />}
								/>
							</Card>
						</Col>
					</Row>
				)}

				<Card
					title="All Domain Extensions"
					extra={
						<Space>
							<Search
								placeholder="Search extensions..."
								allowClear
								value={filters.keyword}
								enterButton={<PiMagnifyingGlassDuotone />}
								style={{ width: 250 }}
								onSearch={handleSearch}
							/>
							<Button
								type="primary"
								icon={<PiPlusDuotone />}
								onClick={() => navigate("/domain-extensions/add")}>
								Add Extension
							</Button>
						</Space>
					}>
					{/* Filters */}
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={6}>
							<Select
								placeholder="Filter by category"
								allowClear
								value={filters.category}
								style={{ width: "100%" }}
								onChange={(value) => handleFilterChange("category", value)}
								suffixIcon={<PiFunnelDuotone />}>
								<Option value="GENERIC">Generic</Option>
								<Option value="COUNTRY">Country</Option>
								<Option value="SPONSORED">Sponsored</Option>
								<Option value="NEW_GENERIC">New Generic</Option>
								<Option value="REGIONAL">Regional</Option>
								<Option value="OTHER">Other</Option>
							</Select>
						</Col>
						<Col span={6}>
							<Select
								placeholder="Filter by status"
								allowClear
								value={filters.status}
								style={{ width: "100%" }}
								onChange={(value) => handleFilterChange("status", value)}
								suffixIcon={<PiFunnelDuotone />}>
								<Option value="ACTIVE">Active</Option>
								<Option value="INACTIVE">Inactive</Option>
								<Option value="COMING_SOON">Coming Soon</Option>
								<Option value="DEPRECATED">Deprecated</Option>
							</Select>
						</Col>
						<Col span={12}>
							{selectedRowKeys.length > 0 && (
								<Space>
									<Text>{selectedRowKeys.length} selected</Text>
									<Dropdown menu={{ items: bulkActions }} placement="bottomRight">
										<Button icon={<PiSelectionAllDuotone />}>Bulk Actions</Button>
									</Dropdown>
								</Space>
							)}
						</Col>
					</Row>

					<Table
						columns={columns}
						dataSource={domainExtensions}
						rowKey="_id"
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} extensions`,
						}}
						onChange={handleTableChange}
						rowSelection={rowSelection}
						scroll={{ x: 1200 }}
						size="small"
					/>
				</Card>
			</div>
		</div>
	);
}

export default DomainExtensionsPage;
